import { useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useOpsService } from '../../../shared/api/opsService';
import { OVERALL_ORDER } from '../components/healthUtils';
import { kernelKeys } from './queryKeys';
import type { CustomerControlPlane, CustomerReconcilerHealth } from '../../../types/backend';

function sortWorstFirst(reconcilers: CustomerReconcilerHealth[]): CustomerReconcilerHealth[] {
  return [...reconcilers].sort((a, b) => {
    const rank = OVERALL_ORDER[a.overall] - OVERALL_ORDER[b.overall];
    return rank !== 0 ? rank : a.domain.localeCompare(b.domain);
  });
}

export interface CustomerControlPlaneState {
  reconcilers: CustomerReconcilerHealth[];
  skippedDomains: string[];
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

export function useCustomerControlPlane(customerId: string | null): CustomerControlPlaneState {
  const ops = useOpsService();

  // Manual fetch only (see useControlPlaneHealth). Keyed by customer so switching
  // the lens is a distinct cache entry; a null customer holds no data.
  const query = useQuery({
    queryKey: kernelKeys.customerControlPlane(customerId),
    queryFn: async () => {
      const result: CustomerControlPlane = await ops.getCustomerControlPlane(customerId as string);
      return {
        reconcilers: sortWorstFirst(result.reconcilers ?? []),
        skippedDomains: result.skipped_domains ?? [],
      };
    },
    enabled: false,
  });

  const { refetch } = query;
  const refresh = useCallback(() => {
    if (customerId) refetch();
  }, [customerId, refetch]);

  return {
    reconcilers: query.data?.reconcilers ?? [],
    skippedDomains: query.data?.skippedDomains ?? [],
    loading: query.isFetching,
    error: query.error
      ? query.error instanceof Error
        ? query.error.message
        : 'Failed to load customer control-plane health'
      : null,
    refresh,
  };
}
